import { BlockType } from '@next-space/fe-api-idl';
import { last } from 'lodash-es';
import type { FC, MouseEvent } from 'react';
import { useRef, useEffect, useCallback, useState } from 'react';
import { UploadArea } from 'src/bitable/table-view/cell/files';
import { Icon } from 'src/common/components/icon';
import { Input } from 'src/common/components/input';
import { ListItemType, ListView } from 'src/common/components/list-view';
import { useOpenModal } from 'src/common/components/next-modal';
import { Tooltip } from 'src/common/components/tooltip';
import { request } from 'src/common/request';
import { useCurrentSpace } from 'src/hooks/space';
import { useCurrentUser } from 'src/hooks/user';
import { blocksActions } from 'src/redux/reducers/blocks';
import { cache, dispatch } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { setAppUiState, useAiPDFHistory } from 'src/services/app';
import { PDFHistoryItem } from './pdf-item';
import { TogglePdfAISidebar } from './toggle-sidebar';
import { Virtuoso } from 'react-virtuoso';
import { isDeleted } from 'src/hooks/block/use-block-status';
import { decodeName } from '@flowus/common/utils';
import { OVERSCAN } from 'src/bitable/const';

const filterList = [
  {
    title: '我上传的 PDF 文件',
    type: 'myUpload',
  },
  {
    title: '无归属的 PDF 文件',
    type: 'withoutOwner',
  },
];

export const PDFSidebar: FC<{ handleUpload: (files: FileList) => string | undefined }> = ({
  handleUpload,
}) => {
  const space = useCurrentSpace();
  const user = useCurrentUser();
  const [value, setValue] = useState('');
  const [filter, setFilter] = useState('');
  const openModal = useOpenModal();
  const historyList = useAiPDFHistory();
  const container = useRef<HTMLDivElement>(null);
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setContainerNode(container.current);
  }, []);

  const fetchPDFList = useCallback(async () => {
    if (!space.uuid) return;
    const res = await request.ai.getSpacePdfHistory(space.uuid);

    const { blocks } = res.recordMap;
    dispatch(blocksActions.update({ blocks: blocks as Record<string, NextBlock>, force: true }));

    setAppUiState({ $pdfAIHistory: res.results });
  }, [space.uuid]);

  const handleChange = (value: string) => {
    setValue(value);
  };

  useEffect(() => {
    void fetchPDFList();
  }, [fetchPDFList]);

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    openModal.dropdown({
      popcorn: event.currentTarget,
      placement: 'right-start',
      content({ onCloseModal }) {
        return (
          <ListView
            className="next-modal w-[228px] py-[5px]"
            items={filterList.map((item) => {
              return {
                type: ListItemType.OPERATION,
                data: {
                  title: item.title,
                  onClick: () => {
                    setFilter(item.type);
                    onCloseModal();
                  },
                },
              };
            })}
          />
        );
      },
    });
  };

  const set = new Set<string>();
  const renderList = historyList.filter((item) => {
    const { blocks } = cache;
    if (isDeleted(item.blockId, blocks)) return false;

    const key = `${item.blockId}&propertyId:${item.propertyId ?? ''}&ossName:${item.ossName}`;
    if (set.has(key)) return false;
    set.add(key);

    const block = blocks[item.blockId];
    if (!block) return false;

    if (filter) {
      const isPDF = block.type === BlockType.FILE;
      if (!isPDF) return false;

      if (
        filter === 'withoutOwner' &&
        space.setting &&
        block.parentId !== space.setting.pdfFolderId
      ) {
        return false;
      }

      if (filter === 'myUpload' && block.createdBy !== user?.uuid) {
        return false;
      }
    }

    const keyword = value.trim();
    let fileName = last(item.ossName.split('/')) ?? '';
    try {
      fileName = decodeName(fileName);
    } catch (err) {
      console.log(err);
    }
    return fileName.includes(keyword);
  });

  return (
    <div className="w-[240px] flex-shrink-0 flex flex-col h-full">
      <div className="h-[55px] w-full flex items-center pl-4 pr-4">
        <TogglePdfAISidebar isSidebar />
      </div>

      <div className="border-t border-r border-grey6 flex-1 overflow-hidden flex flex-col">
        <div className="px-3 py-4 flex items-center justify-between">
          <Input
            inputClassName={'h-8 flex-1 text-black px-0'}
            addonBefore={<Icon name="IcSearch" size="middle" className="ml-3 mr-1 text-grey4" />}
            placeholder="搜索"
            onChange={handleChange}
            value={value}
          />

          <Tooltip popup="筛选">
            <button
              className="ml-2  animate-hover flex p-1 items-center justify-center rounded text-grey3"
              onClick={(event) => {
                event.stopPropagation();
                openMenu(event);
              }}
            >
              <Icon name="IcFilter" size="middle" className="text-grey3 flex-shrink-0" />
            </button>
          </Tooltip>
        </div>
        <div className="flex-1 overflow-auto px-3" ref={container}>
          {filter && (
            <div className="h-10 text-t2 flex items-center justify-between">
              {filterList.find((item) => item.type === filter)?.title}
              <Icon
                name="IcToastClose"
                size="middle"
                className="text-grey3 animate-hover"
                onClick={() => setFilter('')}
              />
            </div>
          )}
          {historyList.length === 0 && (
            <div className="rounded px-2 py-4 bg-grey9 text-t2 text-grey3">
              上传 PDF 文件，在线文档页面 点击
              <Icon name="IcAi" size="middle" className="inline relative translate-y-1 mx-1" />
              开始 AI 阅读
            </div>
          )}

          {containerNode && (
            <Virtuoso
              customScrollParent={containerNode}
              overscan={OVERSCAN}
              increaseViewportBy={OVERSCAN}
              computeItemKey={(index) => index}
              totalCount={renderList.length}
              itemContent={(index) => {
                const item = renderList[index];
                if (!item) return null;
                return (
                  <PDFHistoryItem
                    {...item}
                    key={`${item.blockId}&propertyId:${item.propertyId ?? ''}&ossName:${
                      item.ossName
                    }`}
                  />
                );
              }}
            />
          )}
        </div>
        <UploadArea
          accept="application/pdf"
          className="h-[55px] flex items-center border-t justify-center text-active_color relative"
          onSelect={handleUpload}
        >
          <Icon name="IcBtnNew" size="middle" className="mr-2" />
          上传 PDF
        </UploadArea>
      </div>
    </div>
  );
};
